import { ensureOk } from "@/api/apiError";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { components } from "@/api/generated-types";
import type { AdminSkill, AdminSkillUpdate } from "@/types";
import type { AdminListQuery } from "./useAdminUsers";

export type PaginatedAdminSkills =
  components["schemas"]["PaginatedResponse_AdminSkill_"];

function buildQueryString(params: AdminListQuery): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  search.set("skip", String(params.skip ?? 0));
  search.set("limit", String(params.limit ?? 20));
  return `?${search.toString()}`;
}

async function fetchAdminSkills(
  params: AdminListQuery,
): Promise<PaginatedAdminSkills> {
  const response = await httpClient(`/admin/skills${buildQueryString(params)}`);
  await ensureOk(response);
  return response.json() as Promise<PaginatedAdminSkills>;
}

async function fetchPendingSkillCount(): Promise<number> {
  const response = await httpClient("/admin/skills/pending-count");
  await ensureOk(response);
  const body = (await response.json()) as { count: number };
  return body.count;
}

async function fetchAdminSkill(slug: string): Promise<AdminSkill> {
  const response = await httpClient(
    `/admin/skills/${encodeURIComponent(slug)}`,
  );
  await ensureOk(response);
  return response.json() as Promise<AdminSkill>;
}

/** US-12: admin listing of all skills (any status, including `PENDING` proposals awaiting
 * validation), reserved to `app-admin`. */
export function useAdminSkills(params: AdminListQuery = {}) {
  return useQuery({
    queryKey: ["admin", "skills", params],
    queryFn: () => fetchAdminSkills(params),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** Number of skills awaiting validation — powers the sidebar notification bubble. */
export function usePendingSkillCount(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "skills", "pending-count"],
    queryFn: fetchPendingSkillCount,
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useAdminSkill(slug: string | undefined) {
  return useQuery({
    queryKey: ["admin", "skills", slug],
    queryFn: () => fetchAdminSkill(slug as string),
    enabled: Boolean(slug),
    staleTime: 30 * 1000,
  });
}

export type AdminSkillCreateInput = {
  name: string;
  shortDescription: string;
  fullDescription?: string;
  includedSkills?: string[];
  compatibleHarnesses?: string[];
  sourceType: "ZIP" | "GITHUB";
  repositoryUrl?: string;
  zipFile?: File;
};

function buildCreateFormData(input: AdminSkillCreateInput): FormData {
  const formData = new FormData();
  formData.append("name", input.name);
  formData.append("shortDescription", input.shortDescription);
  if (input.fullDescription) {
    formData.append("fullDescription", input.fullDescription);
  }
  if (input.includedSkills?.length) {
    formData.append("includedSkills", input.includedSkills.join(","));
  }
  if (input.compatibleHarnesses?.length) {
    formData.append("compatibleHarnesses", input.compatibleHarnesses.join(","));
  }
  formData.append("sourceType", input.sourceType);
  if (input.repositoryUrl) {
    formData.append("repositoryUrl", input.repositoryUrl);
  }
  if (input.zipFile) {
    formData.append("zipFile", input.zipFile);
  }
  return formData;
}

async function createAdminSkill(
  input: AdminSkillCreateInput,
): Promise<AdminSkill> {
  const response = await httpClient("/admin/skills", {
    method: "POST",
    body: buildCreateFormData(input),
  });
  await ensureOk(response);
  return response.json() as Promise<AdminSkill>;
}

async function updateAdminSkill(input: {
  slug: string;
  patch: AdminSkillUpdate;
}): Promise<AdminSkill> {
  const response = await httpClient(
    `/admin/skills/${encodeURIComponent(input.slug)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input.patch),
    },
  );
  await ensureOk(response);
  return response.json() as Promise<AdminSkill>;
}

async function deleteAdminSkill(slug: string): Promise<void> {
  const response = await httpClient(
    `/admin/skills/${encodeURIComponent(slug)}`,
    {
      method: "DELETE",
    },
  );
  await ensureOk(response);
}

export type AdminSkillVersionUploadInput = {
  slug: string;
  mode: "NEW_VERSION" | "REPLACE_CURRENT";
  zipFile: File;
};

async function uploadAdminSkillVersion(
  input: AdminSkillVersionUploadInput,
): Promise<AdminSkill> {
  const formData = new FormData();
  formData.append("mode", input.mode);
  formData.append("zipFile", input.zipFile);
  const response = await httpClient(
    `/admin/skills/${encodeURIComponent(input.slug)}/versions`,
    {
      method: "POST",
      body: formData,
    },
  );
  await ensureOk(response);
  return response.json() as Promise<AdminSkill>;
}

/** Creates a skill directly `ACTIVE` (admin bypass — no `PENDING` step, unlike public
 * proposals via `useCreateSkillProposal`). */
export function useCreateAdminSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAdminSkill,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "skills"] });
    },
  });
}

/** Also used to validate a pending proposal: `{ status: "ACTIVE" }` approves it, any other
 * status (or leaving it as `PENDING`) keeps it hidden from the public catalogue. */
export function useUpdateAdminSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAdminSkill,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "skills", variables.slug],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "skills"] });
    },
  });
}

export function useDeleteAdminSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAdminSkill,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "skills"] });
    },
  });
}

export function useUploadAdminSkillVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadAdminSkillVersion,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "skills", variables.slug],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "skills"] });
    },
  });
}
